/**
 * Pub/sub bus for command sequence progress reports.
 *
 * MonitoringWebSocket diverts every progress report here BEFORE the lift-state
 * path (`applyDeltaToState`, the raw message bus, the pre-dispatch inspector),
 * because a report carries the commanded message and is not device state — see
 * `flatbuffers/decoders/sequenceProgress.ts`. `SequenceRunsProvider` is the
 * consumer that turns the reports into runs for the Command Sequences drawer.
 *
 * Zero overhead when no listeners are registered — the emit function
 * early-returns before iterating.
 */

import type { FrontEndMessageT } from "../../../flatbuffers/generated/front-end/front-end-message";
import { logError } from "../../../utils/devLog";

/** One progress report, stamped with when this browser received it. */
export interface SequenceProgressEvent {
  /** The report itself: the commanded message plus its `sequence` table. */
  fem: FrontEndMessageT;
  /** `Date.now()` when the report arrived; elapsed times count on from it. */
  receivedAtMs: number;
}

export type SequenceProgressListener = (event: SequenceProgressEvent) => void;

const listeners = new Set<SequenceProgressListener>();

export function subscribeSequenceProgress(listener: SequenceProgressListener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function emitSequenceProgress(event: SequenceProgressEvent): void {
  if (listeners.size === 0) return;
  listeners.forEach((listener) => {
    // Guard each listener so one buggy subscriber cannot stop delivery to the rest.
    try {
      listener(event);
    } catch (err) {
      logError("[sequenceProgressBus] listener threw:", err);
    }
  });
}

export function hasSequenceProgressListeners(): boolean {
  return listeners.size > 0;
}
